'use client';

import { useTrackingContexts } from '@hc-frontend/core-ui';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { useTrackPageView } from '../../hooks/use-track-page-view';
import type { TrackPageViewProps } from './types';

function equalsReflexiveContext(
  context: SelfDescribingJson[],
  parentContext: SelfDescribingJson[],
) {
  const reflexiveParentContext = parentContext.find((ctx) =>
    ctx.schema.includes('reflexive_funnel'),
  );

  if (!reflexiveParentContext) {
    return true;
  }

  return (
    context.find((ctx) => ctx.schema.includes('reflexive_funnel'))?.data
      .plate_id === reflexiveParentContext.data.plate_id
  );
}

export function TrackPageViewClient({ loadAction }: TrackPageViewProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const trackingContexts = useTrackingContexts();
  const trackPageView = useTrackPageView();

  useEffect(() => {
    const loadContext = async () => {
      if (!loadAction) {
        trackPageView({ context: trackingContexts });
        return;
      }

      const context = await loadAction();
      if (context) {
        if (equalsReflexiveContext(context, trackingContexts)) {
          trackPageView({ context: trackingContexts });
        } else {
          router.refresh();
        }
      }
    };

    loadContext();
  }, [
    pathname,
    loadAction,
    searchParams,
    router,
    trackPageView,
    trackingContexts,
  ]);

  return null;
}
